import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Pencil, Download, ArrowRight, Eye } from "lucide-react"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import {
  PageLayout,
  Button,
  DataTable,
  DetailCard,
  DetailRow,
  StatusBadge,
} from "../common/index.js"

const ViewMLSheet = () => {
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mlSheet, setMLSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const [printingPDF, setPrintingPDF] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    fetchMLSheet()
  }, [mlSheetId])

  const fetchMLSheet = async () => {
    try {
      setLoading(true)
      const response = await mlSheetService.getMLSheet(mlSheetId)

      console.log("ML Sheet API Response:", response.data)

      const sheetData = response.data.ml_sheet || response.data.data
      const shipmentsData = response.data.shipments || []
      const summaryData = response.data.summary || {}

      setMLSheet({
        ...sheetData,
        summary: summaryData,
      })
      setShipments(Array.isArray(shipmentsData) ? shipmentsData : [])

      console.log("ML Sheet Data:", sheetData)
      console.log("Shipments Data:", shipmentsData)
      console.log("Summary Data:", summaryData)
    } catch (error) {
      console.error("Error fetching ML Sheet:", error)
      const status = error.response?.status
      const msg = error.response?.data?.error
      if (status === 403) {
        Alert.error(msg || "You do not have permission to view this ML Sheet")
      } else if (status === 404) {
        Alert.error(msg || "ML Sheet not found")
      } else {
        Alert.error(msg || "Failed to load ML Sheet")
      }
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  const handleGeneratePDF = async () => {
    try {
      setDownloadingPDF(true)
      const response = await mlSheetService.generatePDF(mlSheetId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `ml-sheet-${mlSheetId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("PDF downloaded successfully")
    } catch (error) {
      console.error("Error generating PDF:", error)
      Alert.error("Failed to generate PDF")
    } finally {
      setDownloadingPDF(false)
    }
  }

  const handlePrintPDF = async () => {
    try {
      setPrintingPDF(true)
      const response = await mlSheetService.generatePDF(mlSheetId)

      const blob = new Blob([response.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)

      const printWindow = window.open(url, "_blank")

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
        Alert.success("PDF opened for printing")
      } else {
        Alert.error("Please allow pop-ups to print")
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1000)
    } catch (error) {
      console.error("Error printing PDF:", error)
      Alert.error("Failed to print PDF")
    } finally {
      setPrintingPDF(false)
    }
  }

  const getMLSheetStatusVariant = (status) => {
    const statusUpper = (status || "").toUpperCase()
    return statusUpper === "OPEN" ? "success" : "danger"
  }

  const getShipmentStatusVariant = (status) => {
    const statusMap = {
      booked: "info",
      dispatched: "warning",
      delivered: "success",
      cancelled: "danger",
    }
    return statusMap[status?.toLowerCase()] || "default"
  }

  const canEdit = () => {
    return user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  }

  const canUpdateStatus = () => {
    if (!user) return false

    const allowedRoles = ["System Admin", "Admin", "Accountant"]

    if (allowedRoles.includes(user.role)) {
      return true
    }

    if (user.role === "Agent" && mlSheet?.access_users) {
      const hasAccess = mlSheet.access_users.some((accessUser) => {
        if (typeof accessUser === "string") {
          return accessUser === user.user_id
        }
        return (
          accessUser.user_id === user.user_id ||
          accessUser.id === user.user_id ||
          accessUser === user.user_id
        )
      })

      return hasAccess
    }

    return false
  }

  const canChangeMLSheetStatus = () => {
    if (!user) return false
    if (["System Admin", "Admin", "Accountant"].includes(user.role)) return true
    if (user.role === "Agent" && mlSheet?.access_users) {
      return mlSheet.access_users.some((accessUser) => {
        if (typeof accessUser === "string") return accessUser === user.user_id
        return accessUser.user_id === user.user_id || accessUser.id === user.user_id
      })
    }
    return false
  }

  const handleMLSheetStatusChange = async (newStatus) => {
    const currentStatus = (mlSheet.status || "").toUpperCase()
    if (newStatus === currentStatus) return

    if (!window.confirm(`Change ML Sheet status to ${newStatus}?`)) return

    try {
      setUpdatingStatus(true)
      const routeId = String(mlSheet.ml_sheet_id || mlSheetId).replace(/^ML-/i, "")
      await mlSheetService.updateMLSheetStatus(routeId, newStatus)
      Alert.success("ML Sheet status updated successfully")
      await fetchMLSheet()
    } catch (error) {
      console.error("Error updating ML Sheet status:", error)
      const status = error.response?.status
      const msg = error.response?.data?.error
      if (status === 403) {
        Alert.error(msg || "You do not have permission to update this ML Sheet status")
      } else if (status === 404) {
        Alert.error(msg || "ML Sheet not found")
      } else {
        Alert.error(msg || "Failed to update ML Sheet status")
      }
    } finally {
      setUpdatingStatus(false)
    }
  }

  const formatFromLocations = (sheet) => {
    if (sheet.from_locations && sheet.from_locations.length > 0) {
      return sheet.from_locations
        .map((loc) => loc.location || loc.hub_name || loc.name)
        .join(", ")
    }
    return (
      sheet.from_location?.location ||
      sheet.from_location?.hub_name ||
      sheet.from_location ||
      "N/A"
    )
  }

  const formatToLocations = (sheet) => {
    if (sheet.to_locations && sheet.to_locations.length > 0) {
      return sheet.to_locations.map((loc) => loc.location || loc.hub_name || loc.name).join(", ")
    }
    return (
      sheet.to_location?.location || sheet.to_location?.hub_name || sheet.to_location || "N/A"
    )
  }

  if (loading) {
    return (
      <PageLayout title="ML Sheet Details" maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading ML Sheet...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!mlSheet) {
    return (
      <PageLayout title="ML Sheet Details" maxWidth="max-w-7xl">
        <DetailCard>
          <p className="text-center text-gray-600">ML Sheet not found</p>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => navigate("/ml-sheets")}>
              Back to ML Sheets
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    )
  }

  const shipmentColumns = [
    {
      key: "sr_no",
      label: "Sr No",
      width: "6%",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (_, index) => index + 1,
    },
    {
      key: "shipment_id",
      label: "Shipment ID",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <span className="font-regular text-secondary cursor-pointer">{shipment.shipment_id}</span>
      ),
    },
    {
      key: "sender",
      label: "Sender",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <>
          <div className="font-regular text-secondary">{shipment.sender_name}</div>
          <div className="text-sm text-secondary">{shipment.sender_contact}</div>
        </>
      ),
    },
    {
      key: "receiver",
      label: "Receiver",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <>
          <div className="font-regular text-secondary">{shipment.receiver_name}</div>
          <div className="text-sm text-secondary">{shipment.receiver_contact}</div>
        </>
      ),
    },
    {
      key: "route",
      label: "Route",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <div className="flex items-center justify-center gap-2 font-regular text-secondary whitespace-nowrap">
          <span>{shipment.sender_location || "N/A"}</span>
          <span>→</span>
          <span>{shipment.receiver_location || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "packages",
      label: "Packages",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <span className="inline-block px-3 py-1 text-secondary font-regular text-sm">
          {shipment.no_of_articles}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => `₹${shipment.total_amount?.toFixed(2)}`,
    },
    {
      key: "balance",
      label: "Balance",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) =>
        shipment.balance_amount > 0 ? (
          <span className="font-regular text-secondary">
            ₹{shipment.balance_amount?.toFixed(2)}
          </span>
        ) : (
          <StatusBadge status="paid" variant="success" label="Paid" />
        ),
    },
    {
      key: "status",
      label: "Status",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (shipment) => (
        <StatusBadge status={shipment.status} variant={getShipmentStatusVariant(shipment.status)} />
      ),
    },
  ]

  const getShipmentActions = (shipment) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => navigate(`/shipments/view/${shipment.shipment_id}`),
    },
  ]

  const shipmentMobileCardRender = (shipment, index) => (
    <div>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="text-xs text-gray-500 font-semibold">#{index + 1}</div>
          <div className="font-bold text-blue-600 text-lg">{shipment.shipment_id}</div>
        </div>
        <StatusBadge
          status={shipment.status}
          variant={getShipmentStatusVariant(shipment.status)}
        />
      </div>

      <div className="space-y-2 mb-3">
        <div className="bg-gray-50 rounded p-2">
          <div className="text-xs text-gray-600 font-semibold">Sender</div>
          <div className="font-semibold text-gray-800">{shipment.sender_name}</div>
          <div className="text-sm text-gray-600">{shipment.sender_contact}</div>
          <div className="text-xs text-gray-500">{shipment.sender_location || "N/A"}</div>
        </div>

        <div className="text-center text-gray-400">↓</div>

        <div className="bg-gray-50 rounded p-2">
          <div className="text-xs text-gray-600 font-semibold">Receiver</div>
          <div className="font-semibold text-gray-800">{shipment.receiver_name}</div>
          <div className="text-sm text-gray-600">{shipment.receiver_contact}</div>
          <div className="text-xs text-gray-500">{shipment.receiver_location || "N/A"}</div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
        <div className="flex gap-3">
          <div>
            <div className="text-xs text-gray-600">Packages</div>
            <div className="font-bold text-gray-800">{shipment.no_of_articles}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600">Amount</div>
            <div className="font-bold text-gray-800">₹{shipment.total_amount?.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600">Balance</div>
            <div
              className={`font-bold ${shipment.balance_amount > 0 ? "text-red-600" : "text-green-600"}`}
            >
              {shipment.balance_amount > 0
                ? `₹${shipment.balance_amount?.toFixed(2)}`
                : "Paid"}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/shipments/view/${shipment.shipment_id}`)
          }}
        >
          View
        </Button>
      </div>
    </div>
  )

  return (
    <PageLayout
      title={`ML Sheet # ${mlSheet.ml_sheet_id}`}
      maxWidth="max-w-7xl"
      actions={
        <>
          <Button
            icon={Download}
            onClick={handleGeneratePDF}
            loading={downloadingPDF}
            disabled={downloadingPDF}
          >
            Download PDF
          </Button>
          <Button
            icon={Download}
            onClick={handlePrintPDF}
            loading={printingPDF}
            disabled={printingPDF}
          >
            Print PDF
          </Button>
          {canUpdateStatus() && (
            <Button
              icon={Pencil}
              onClick={() => navigate(`/ml-sheets/view/${mlSheet.ml_sheet_id}/status-update`)}
              title="Update shipment statuses in bulk"
            >
              Update Status
            </Button>
          )}
          {canEdit() && (
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => navigate(`/ml-sheets/edit/${mlSheet.ml_sheet_id}`)}
            >
              Edit
            </Button>
          )}
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/ml-sheets")}>
            Back
          </Button>
        </>
      }
    >
      <p className="text-tertiary text-sm sm:text-base mb-6">
        Created on{" "}
        {new Date(mlSheet.created_at).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <DetailCard className="border-l-4 border-l-brand">
          <DetailRow
            label="Status"
            value={
              canChangeMLSheetStatus() ? (
                <select
                  value={(mlSheet.status || "").toUpperCase()}
                  onChange={(e) => handleMLSheetStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-brand uppercase disabled:opacity-50"
                >
                  <option value="OPEN">OPEN</option>
                  <option value="CLOSE">CLOSE</option>
                </select>
              ) : (
                <StatusBadge
                  status={mlSheet.status}
                  variant={getMLSheetStatusVariant(mlSheet.status)}
                  label={(mlSheet.status || "").toUpperCase()}
                />
              )
            }
          />
        </DetailCard>

        <DetailCard className="border-l-4 border-l-blue-500">
          <DetailRow
            label="Total Shipments"
            value={mlSheet.summary?.total_shipments || shipments.length || 0}
          />
        </DetailCard>

        <DetailCard className="border-l-4 border-l-emerald-500">
          <DetailRow
            label="Total Packages"
            value={
              mlSheet.summary?.total_packages ||
              shipments.reduce((sum, s) => sum + (s.no_of_articles || 0), 0)
            }
          />
        </DetailCard>

        <DetailCard className="border-l-4 border-l-purple-500">
          <DetailRow
            label="Total Amount"
            value={`₹${(
              mlSheet.summary?.total_amount ||
              shipments.reduce((sum, s) => sum + (s.total_amount || 0), 0)
            ).toFixed(2)}`}
          />
        </DetailCard>

        {mlSheet.summary?.total_balance > 0 && (
          <DetailCard className="border-l-4 border-l-gray-500">
            <DetailRow
              label="Total Balance Due"
              value={`₹${mlSheet.summary.total_balance.toFixed(2)}`}
            />
          </DetailCard>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <DetailCard title="Route Information">
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="bg-gray-50 col-span-12 md:col-span-5 rounded-sm p-3 border border-primary">
              <div className="text-secondary text-xs font-semibold uppercase mb-1">
                From Location
              </div>
              <div className="text-sm font-regular text-secondary">
                {formatFromLocations(mlSheet)}
              </div>
            </div>

            <div className="col-span-12 md:col-span-2 flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-secondary" />
            </div>

            <div className="bg-gray-50 col-span-12 md:col-span-5 rounded-sm p-3 border border-primary">
              <div className="text-secondary text-xs font-semibold uppercase mb-1">
                To Location
              </div>
              <div className="text-sm font-regular text-secondary">{formatToLocations(mlSheet)}</div>
            </div>
          </div>
        </DetailCard>

        <DetailCard title="Vehicle & Driver Information">
          <DetailRow label="Vehicle Number" value={mlSheet.vehicle_number || "N/A"} />
          <DetailRow label="Driver Contact" value={mlSheet.driver_contact || "N/A"} />
        </DetailCard>

        <DetailCard title={`Access Users (${mlSheet.access_users?.length || 0})`}>
          <div className="flex flex-wrap gap-3">
            {mlSheet.access_users && mlSheet.access_users.length > 0 ? (
              mlSheet.access_users.map((accessUser) => {
                const userId = accessUser.user_id || accessUser.id || accessUser
                const userName = accessUser.name || `User ${userId}`
                return (
                  <button
                    key={userId}
                    type="button"
                    onClick={() => navigate(`/users/view/${userId}`)}
                    className="px-4 py-2 flex items-center justify-center gap-2 rounded-sm font-regular text-sm text-secondary shadow-sm transition-all border border-gray-400"
                  >
                    <span className="leading-none">{userName}</span>
                    <ArrowRight className="w-4 h-4 text-secondary flex-shrink-0" />
                  </button>
                )
              })
            ) : (
              <p className="text-gray-500">No access users assigned</p>
            )}
          </div>
        </DetailCard>
      </div>

      <DetailCard title={`Included Shipments (${shipments.length})`}>
        <DataTable
          columns={shipmentColumns}
          data={shipments}
          rowKey="id"
          actions={getShipmentActions}
          mobileCardRender={shipmentMobileCardRender}
          emptyMessage="No shipments in this ML Sheet"
        />
      </DetailCard>
    </PageLayout>
  )
}

export default ViewMLSheet
